import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaPaperPlane,
  FaCheckCircle,
  FaLinkedin,
  FaArrowRight,
  FaHeadset
} from 'react-icons/fa';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .fade-up, .scale-up, .slide-in-left, .slide-in-right, .slide-in-blur-left, .slide-in-blur-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch('https://developer05.pythonanywhere.com/api/contact/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!response.ok) {
        throw new Error('Failed to send');
      }
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: ''
        });
      }, 4000);
    } catch (err) {
      alert('There was a problem sending your message. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const contactMethods = [
    {
      icon: <FaPhone />,
      title: "Call Us",
      info: "+91 9487924487",
      subInfo: "+91 9443513487",
      action: "tel:+919487924487"
    },
    {
      icon: <FaEnvelope />,
      title: "Email Us",
      info: "robologautomation@gmail.com",
      subInfo: "We respond within 24 hours",
      action: "mailto:robologautomation@gmail.com"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Us",
      info: "Dharmapuri, Tamil Nadu",
      subInfo: "1/312/1, Emakuttiyur",
      action: "https://www.google.com/maps/place/12%C2%B005'56.3%22N+78%C2%B008'58.0%22E"
    },
    {
      icon: <FaClock />,
      title: "Business Hours",
      info: "Mon-Fri: 9AM - 8PM",
      subInfo: "Sat: 10AM - 6PM",
      action: null
    }
  ];

  return (
    <div className="contact-page">
      <SEO
        title="Contact Robolog Automation - Get Industrial Automation Solutions"
        description="Contact Robolog Automation for industrial automation solutions, PCB test jigs, robotics integration, and IoT platforms. Located in Dharmapuri, Tamil Nadu. Call +91 9487924487 or email robologautomation@gmail.com for consultation."
        keywords="contact robolog automation, industrial automation consultation, PCB test jig inquiry, automation project quote, robolog contact, automation company contact, industrial automation services inquiry, Dharmapuri automation company"
        ogImage="/Home-page.png"
      />
      {/* Hero Section - Compact */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content fade-up">
            <span className="hero-badge">
              <FaHeadset /> Available for Projects
            </span>
            <h1>Let's Build Something Great Together</h1>
            <p>
              Have a project in mind? Need automation solutions? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Contact Cards */}
      <section className="quick-contact">
        <div className="container">
          <div className="contact-cards">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.action || '#'}
                className={`contact-card fade-up ${!method.action ? 'no-link' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                target={method.action?.startsWith('http') ? '_blank' : undefined}
                rel={method.action?.startsWith('http') ? 'noreferrer' : undefined}
              >
                <div className="contact-card-icon">
                  {method.icon}
                </div>
                <div className="contact-card-text">
                  <h3>{method.title}</h3>
                  <p className="main-info">{method.info}</p>
                  <p className="sub-info">{method.subInfo}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-wrapper">
            {/* Form Section */}
            <div className="contact-form-section slide-in-blur-left">
              <div className="form-header">
                <h2>Send us a Message</h2>
                <p>Fill out the form and we'll get back to you within 24 hours.</p>
              </div>

              {isSubmitted ? (
                <div className="success-message">
                  <div className="success-icon">
                    <FaCheckCircle />
                  </div>
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="name">Full Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="Your name"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="your@email.com"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="company">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your company"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 XXXXX XXXXX"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="service">What can we help with?</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a service</option>
                      <option value="pcb-test-jigs">PCB Test Jigs (ICT/FCT)</option>
                      <option value="robotic-automation">Robotic & SPM Cells</option>
                      <option value="monitoring-analytics">Monitoring & Analytics</option>
                      <option value="plc-panels">PLC & Control Panels</option>
                      <option value="iot-solutions">IIoT Solutions</option>
                      <option value="software-development">Software Development</option>
                      <option value="consulting">Consulting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      placeholder="Tell us about your project or requirements..."
                    ></textarea>
                  </div>

                  <button type="submit" className="btn btn-primary submit-btn" disabled={isLoading}>
                    {isLoading ? (
                      <>Sending...</>
                    ) : (
                      <>
                        <FaPaperPlane />
                        Send Message
                        <FaArrowRight />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info Section */}
            <div className="contact-info-section slide-in-blur-right">
              <div className="info-card">
                <h3>Why Work With Us?</h3>
                <ul className="benefits-list">
                  <li>
                    <FaCheckCircle />
                    <span>70+ automation projects delivered</span>
                  </li>
                  <li>
                    <FaCheckCircle />
                    <span>45+ industrial customers</span>
                  </li>
                  <li>
                    <FaCheckCircle />
                    <span>End-to-end project execution</span>
                  </li>
                  <li>
                    <FaCheckCircle />
                    <span>Ongoing support & maintenance</span>
                  </li>
                </ul>
              </div>

              <div className="info-card social-card">
                <h3>Connect With Us</h3>
                <p>Follow us for updates and industry insights</p>
                <div className="social-buttons">
                  <a href="https://linkedin.com" className="social-btn linkedin" target="_blank" rel="noreferrer">
                    <FaLinkedin />
                    <span>LinkedIn</span>
                  </a>
                </div>
              </div>

              <div className="info-card map-card">
                <h3>Our Location</h3>
                <div className="mini-map">
                  <iframe
                    title="Robolog Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3910.758!2d78.149451!3d12.098964!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x3f4a24c94e55c5c5!2zMTLCsDA1JzU2LjMiTiA3OMKwMDgnNTguMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="150"
                    style={{ border: 0, borderRadius: '8px' }}
                    allowFullScreen=""
                    loading="lazy"
                  ></iframe>
                </div>
                <a
                  href="https://www.google.com/maps/place/12%C2%B005'56.3%22N+78%C2%B008'58.0%22E"
                  target="_blank"
                  rel="noreferrer"
                  className="map-link"
                >
                  Open in Google Maps
                  <FaArrowRight />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
